import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Edit2, Check, X, ChevronDown, ChevronUp, Search } from 'lucide-react';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const ALL_DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const emptyFood = () => ({ name: '', quantity: '', calories: '', protein: '', carbs: '', fat: '', preparation: 'cru', alternatives: [] });
const emptyMeal = () => ({ name: '', time: '', days: [...ALL_DAYS], foods: [emptyFood()] });
const emptyTemplate = () => ({ name: '', description: '', notes: '', meals: [emptyMeal()] });

const calcTemplateCals = (meals) =>
  (meals || []).reduce((sum, m) => sum + (m.foods || []).reduce((s, f) => s + (Number(f.calories) || 0), 0), 0);

function FoodSearch({ value, onChange, onSelectFood, inputCls, labelCls }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef(null);
  const token = sessionStorage.getItem('admin_token');

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    onChange(q);
    clearTimeout(timerRef.current);
    if (q.length >= 2) {
      timerRef.current = setTimeout(async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/admin/despensa`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { search: q },
          });
          setSuggestions(res.data.slice(0, 8));
          setShowDropdown(true);
        } catch {}
      }, 300);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (item) => {
    setQuery(item.name);
    setShowDropdown(false);
    onSelectFood(item);
  };

  return (
    <div className="relative">
      <label className={labelCls}>Alimento</label>
      <div className="relative">
        <input
          value={query}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          className={inputCls}
          placeholder="Buscar na despensa..."
        />
        <Search size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] pointer-events-none" />
      </div>
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 bg-[#1A1A1A] border border-white/10 max-h-48 overflow-y-auto">
          {suggestions.map(item => (
            <button key={item.id} type="button" onMouseDown={() => handleSelect(item)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between">
              <span>{item.name}</span>
              <span className="text-xs text-[#555]">{item.calories ? `${item.calories} kcal` : ''} {item.portion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDietaTemplates() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [templates, setTemplates] = useState([]);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(emptyTemplate());
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editForm, setEditForm] = useState({});

  const inputCls = 'bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none px-3 py-2 text-sm w-full transition-colors';
  const labelCls = 'block text-xs text-[#555] uppercase tracking-widest mb-1';

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadTemplates();
  // eslint-disable-next-line
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/diet-templates`, { headers });
      setTemplates(res.data);
    } catch {}
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/diet-templates`, { ...form, calories: calcTemplateCals(form.meals) }, { headers });
      setTemplates(prev => [res.data, ...prev]);
      setForm(emptyTemplate());
      setShowAddForm(false);
    } catch (err) { alert(err.response?.data?.detail || 'Erro ao criar template.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este template de dieta?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/diet-templates/${id}`, { headers });
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch { alert('Erro ao remover.'); }
  };

  const startEdit = (tpl) => {
    setEditingTemplate(tpl.id);
    setEditForm({
      ...tpl,
      meals: tpl.meals ? tpl.meals.map(m => ({
        ...m,
        foods: m.foods ? m.foods.map(f => ({ ...f, alternatives: f.alternatives || [] })) : [emptyFood()],
      })) : [emptyMeal()],
    });
    setExpandedTemplate(tpl.id);
  };

  const saveEdit = async (id) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/diet-templates/${id}`, { ...editForm, calories: calcTemplateCals(editForm.meals) }, { headers });
      setTemplates(prev => prev.map(t => t.id === id ? res.data : t));
      setEditingTemplate(null);
    } catch { alert('Erro ao salvar template.'); }
  };

  // ── Form helpers ──
  const setMeal = (meals, setMeals, mi, field, val) =>
    setMeals(prev => ({ ...prev, meals: meals.map((m, j) => j === mi ? { ...m, [field]: val } : m) }));

  // For add form
  const updateFormMeal = (mi, field, val) =>
    setForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, [field]: val } : m) }));
  const addFormMeal = () => setForm(f => ({ ...f, meals: [...f.meals, emptyMeal()] }));
  const removeFormMeal = (mi) => setForm(f => ({ ...f, meals: f.meals.filter((_, j) => j !== mi) }));
  const updateFormFood = (mi, fi, field, val) =>
    setForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? { ...food, [field]: val } : food) } : m) }));
  const addFormFood = (mi) => setForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: [...m.foods, emptyFood()] } : m) }));
  const removeFormFood = (mi, fi) => setForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.filter((_, k) => k !== fi) } : m) }));
  const selectDespensaFood = (mi, fi, item) =>
    setForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? {
      ...food, name: item.name, calories: item.calories || '', protein: item.protein || '', carbs: item.carbs || '', fat: item.fat || '',
    } : food) } : m) }));

  // For edit form
  const updateEditMeal = (mi, field, val) =>
    setEditForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, [field]: val } : m) }));
  const addEditMeal = () => setEditForm(f => ({ ...f, meals: [...f.meals, emptyMeal()] }));
  const removeEditMeal = (mi) => setEditForm(f => ({ ...f, meals: f.meals.filter((_, j) => j !== mi) }));
  const updateEditFood = (mi, fi, field, val) =>
    setEditForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? { ...food, [field]: val } : food) } : m) }));
  const addEditFood = (mi) => setEditForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: [...m.foods, emptyFood()] } : m) }));
  const removeEditFood = (mi, fi) => setEditForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.filter((_, k) => k !== fi) } : m) }));
  const selectDespensaFoodEdit = (mi, fi, item) =>
    setEditForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? {
      ...food, name: item.name, calories: item.calories || '', protein: item.protein || '', carbs: item.carbs || '', fat: item.fat || '',
    } : food) } : m) }));

  // Alternatives helpers
  const addFormAlt = (mi, fi) =>
    setForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? { ...food, alternatives: [...(food.alternatives || []), { name: '', quantity: '' }] } : food) } : m) }));
  const removeFormAlt = (mi, fi, ai) =>
    setForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? { ...food, alternatives: food.alternatives.filter((_, l) => l !== ai) } : food) } : m) }));
  const updateFormAlt = (mi, fi, ai, field, val) =>
    setForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? { ...food, alternatives: food.alternatives.map((a, l) => l === ai ? { ...a, [field]: val } : a) } : food) } : m) }));

  const addEditAlt = (mi, fi) =>
    setEditForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? { ...food, alternatives: [...(food.alternatives || []), { name: '', quantity: '' }] } : food) } : m) }));
  const removeEditAlt = (mi, fi, ai) =>
    setEditForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? { ...food, alternatives: food.alternatives.filter((_, l) => l !== ai) } : food) } : m) }));
  const updateEditAlt = (mi, fi, ai, field, val) =>
    setEditForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((food, k) => k === fi ? { ...food, alternatives: food.alternatives.map((a, l) => l === ai ? { ...a, [field]: val } : a) } : food) } : m) }));

  const renderMealsForm = (meals, updateMeal, addMeal, removeMeal, updateFood, addFood, removeFood, selectFood, addAlt, removeAlt, updateAlt) => (
    <div className="flex flex-col gap-4 mt-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#555] uppercase tracking-widest">Refeições</span>
        <button type="button" onClick={addMeal}
          className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors">
          <Plus size={11} /> Refeição
        </button>
      </div>
      {meals.map((meal, mi) => (
        <div key={mi} className="bg-[#0A0A0A] border border-white/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-[#555]">Refeição {mi + 1}</span>
            {meals.length > 1 && (
              <button type="button" onClick={() => removeMeal(mi)} className="text-[#555] hover:text-red-400"><Trash2 size={12} /></button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={labelCls}>Nome</label>
              <input value={meal.name} onChange={e => updateMeal(mi, 'name', e.target.value)} className={inputCls} placeholder="Café da manhã" />
            </div>
            <div>
              <label className={labelCls}>Horário sugerido</label>
              <input type="time" value={meal.time} onChange={e => updateMeal(mi, 'time', e.target.value)} className={inputCls} placeholder="07:00" />
            </div>
          </div>
          {/* Days of week */}
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            <span className={`${labelCls} mb-0 mr-1`}>Dias:</span>
            {ALL_DAYS.map(d => {
              const active = (meal.days || ALL_DAYS).includes(d);
              return (
                <button key={d} type="button" onClick={() => {
                  const cur = meal.days || ALL_DAYS;
                  updateMeal(mi, 'days', active ? cur.filter(x => x !== d) : [...cur, d]);
                }} className={`px-2 py-0.5 text-xs border transition-colors ${active ? 'border-white/40 text-white' : 'border-white/10 text-[#444] hover:text-[#888]'}`}>
                  {d.slice(0, 3)}
                </button>
              );
            })}
            <span className="text-xs text-[#444] ml-1">{(meal.days || ALL_DAYS).length}×/sem</span>
          </div>

          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#555] uppercase tracking-widest">Alimentos</span>
            <button type="button" onClick={() => addFood(mi)}
              className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-2 py-1 transition-colors">
              <Plus size={11} /> Alimento
            </button>
          </div>

          {meal.foods.map((food, fi) => (
            <div key={fi} className="bg-[#121212] border border-white/5 p-3 mb-2">
              <div className="flex justify-end mb-2">
                {meal.foods.length > 1 && (
                  <button type="button" onClick={() => removeFood(mi, fi)} className="text-[#555] hover:text-red-400"><Trash2 size={11} /></button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                <div className="col-span-2 sm:col-span-2">
                  <FoodSearch
                    value={food.name}
                    onChange={val => updateFood(mi, fi, 'name', val)}
                    onSelectFood={item => selectFood(mi, fi, item)}
                    inputCls={inputCls}
                    labelCls={labelCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Qtd</label>
                  <input value={food.quantity} onChange={e => updateFood(mi, fi, 'quantity', e.target.value)} className={inputCls} placeholder="100g" />
                </div>
                <div>
                  <label className={labelCls}>Estado</label>
                  <select value={food.preparation || 'cru'} onChange={e => updateFood(mi, fi, 'preparation', e.target.value)} className={inputCls}>
                    <option value="cru">Cru</option>
                    <option value="cozido">Cozido</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Kcal</label>
                  <input type="number" value={food.calories} onChange={e => updateFood(mi, fi, 'calories', e.target.value)} className={inputCls} placeholder="0" />
                </div>
                <div>
                  <label className={labelCls}>Prot (g)</label>
                  <input type="number" value={food.protein} onChange={e => updateFood(mi, fi, 'protein', e.target.value)} className={inputCls} placeholder="0" />
                </div>
                <div>
                  <label className={labelCls}>Carb (g)</label>
                  <input type="number" value={food.carbs} onChange={e => updateFood(mi, fi, 'carbs', e.target.value)} className={inputCls} placeholder="0" />
                </div>
                <div>
                  <label className={labelCls}>Gord (g)</label>
                  <input type="number" value={food.fat} onChange={e => updateFood(mi, fi, 'fat', e.target.value)} className={inputCls} placeholder="0" />
                </div>
              </div>

              {/* Alternatives */}
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#555]">Alternativas</span>
                  <button type="button" onClick={() => addAlt(mi, fi)}
                    className="text-xs text-[#555] hover:text-white border border-white/5 px-2 py-0.5 transition-colors flex items-center gap-1">
                    <Plus size={10} /> Alt
                  </button>
                </div>
                {(food.alternatives || []).map((alt, ai) => (
                  <div key={ai} className="grid grid-cols-3 gap-2 items-end">
                    <div className="col-span-2">
                      <input value={alt.name} onChange={e => updateAlt(mi, fi, ai, 'name', e.target.value)} className={inputCls} placeholder="Nome da alternativa" />
                    </div>
                    <div className="flex gap-1">
                      <input value={alt.quantity} onChange={e => updateAlt(mi, fi, ai, 'quantity', e.target.value)} className={inputCls} placeholder="Qtd" />
                      <button type="button" onClick={() => removeAlt(mi, fi, ai)} className="text-[#555] hover:text-red-400 pb-2"><X size={11} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin')} className="text-[#555] hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold uppercase tracking-tight">Templates de Dieta</h1>
            <p className="text-xs text-[#555] mt-0.5">Modelos de planos alimentares reutilizáveis</p>
          </div>
        </div>

        <div className="flex justify-end mb-6">
          <button onClick={() => setShowAddForm(v => !v)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors">
            <Plus size={14} /> {showAddForm ? 'Cancelar' : 'Novo template'}
          </button>
        </div>

        {showAddForm && (
          <form onSubmit={handleAdd} className="bg-[#121212] border border-white/5 p-6 mb-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4">Novo template de dieta</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Nome</label>
                <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className={inputCls} placeholder="Dieta para hipertrofia" />
              </div>
              <div className="flex items-end">
                <div className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full text-[#555]">
                  <span className="text-xs text-[#555] uppercase tracking-widest block mb-0.5">Calorias totais (calculado)</span>
                  <span className="text-white font-medium">{calcTemplateCals(form.meals)} kcal</span>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Descrição</label>
                <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className={inputCls} placeholder="Opcional" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notas</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className={`${inputCls} min-h-[60px] resize-y`} placeholder="Observações gerais..." />
              </div>
            </div>
            {renderMealsForm(form.meals, updateFormMeal, addFormMeal, removeFormMeal, updateFormFood, addFormFood, removeFormFood, selectDespensaFood, addFormAlt, removeFormAlt, updateFormAlt)}
            <div className="mt-4">
              <button type="submit" disabled={saving}
                className="px-6 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50">
                {saving ? 'Salvando...' : 'Criar template'}
              </button>
            </div>
          </form>
        )}

        {templates.length === 0 ? (
          <div className="text-center py-16 text-[#555] text-sm">Nenhum template de dieta criado ainda.</div>
        ) : (
          <div className="flex flex-col gap-3">
            {templates.map(tpl => (
              <div key={tpl.id} className="bg-[#121212] border border-white/5">
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => setExpandedTemplate(expandedTemplate === tpl.id ? null : tpl.id)}
                      className="text-[#555] hover:text-white transition-colors flex-shrink-0">
                      {expandedTemplate === tpl.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{tpl.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {calcTemplateCals(tpl.meals) > 0 && <span className="text-xs text-[#555] border border-white/10 px-2 py-0.5">{calcTemplateCals(tpl.meals)} kcal/dia</span>}
                        {tpl.description && <span className="text-xs text-[#555] truncate">{tpl.description}</span>}
                        <span className="text-xs text-[#555]">{tpl.meals?.length || 0} refeições</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => startEdit(tpl)} className="text-[#555] hover:text-white transition-colors"><Edit2 size={13} /></button>
                    <button onClick={() => handleDelete(tpl.id)} className="text-[#555] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                  </div>
                </div>

                {expandedTemplate === tpl.id && (
                  <div className="border-t border-white/5 px-5 pb-5 pt-4">
                    {editingTemplate === tpl.id ? (
                      <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className={labelCls}>Nome</label>
                            <input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} className={inputCls} />
                          </div>
                          <div className="flex items-end">
                            <div className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full">
                              <span className="text-xs text-[#555] uppercase tracking-widest block mb-0.5">Calorias totais (calculado)</span>
                              <span className="text-white font-medium">{calcTemplateCals(editForm.meals)} kcal</span>
                            </div>
                          </div>
                          <div className="sm:col-span-2">
                            <label className={labelCls}>Descrição</label>
                            <input value={editForm.description || ''} onChange={e => setEditForm(f => ({...f, description: e.target.value}))} className={inputCls} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className={labelCls}>Notas</label>
                            <textarea value={editForm.notes || ''} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} className={`${inputCls} min-h-[60px] resize-y`} />
                          </div>
                        </div>
                        {renderMealsForm(editForm.meals || [], updateEditMeal, addEditMeal, removeEditMeal, updateEditFood, addEditFood, removeEditFood, selectDespensaFoodEdit, addEditAlt, removeEditAlt, updateEditAlt)}
                        <div className="flex gap-2 mt-4">
                          <button onClick={() => saveEdit(tpl.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors">
                            <Check size={13} /> Salvar
                          </button>
                          <button onClick={() => setEditingTemplate(null)}
                            className="flex items-center gap-2 px-4 py-2 border border-white/10 text-sm text-[#A3A3A3] hover:text-white transition-colors">
                            <X size={13} /> Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {tpl.notes && (
                          <p className="text-sm text-[#A3A3A3] bg-[#0A0A0A] border border-white/5 p-3">{tpl.notes}</p>
                        )}
                        {(!tpl.meals || tpl.meals.length === 0) ? (
                          <p className="text-sm text-[#555]">Nenhuma refeição neste template.</p>
                        ) : (
                          tpl.meals.map((meal, mi) => (
                            <div key={mi} className="bg-[#0A0A0A] border border-white/5 p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium text-sm">{meal.name || `Refeição ${mi + 1}`}</span>
                                {meal.time && <span className="text-xs text-[#555]">{meal.time}</span>}
                              </div>
                              {meal.foods && meal.foods.length > 0 && (
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="border-b border-white/5">
                                      <th className="text-left pb-1 text-xs text-[#555] font-medium">Alimento</th>
                                      <th className="text-right pb-1 text-xs text-[#555] font-medium">Qtd</th>
                                      <th className="text-right pb-1 text-xs text-[#555] font-medium">Kcal</th>
                                      <th className="text-right pb-1 text-xs text-[#555] font-medium hidden sm:table-cell">Prot</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {meal.foods.map((f, fi) => (
                                      <tr key={fi} className="border-b border-white/5 last:border-0">
                                        <td className="py-1.5 pr-2">
                                          <span>{f.name}</span>
                                          {f.alternatives && f.alternatives.length > 0 && (
                                            <span className="ml-2 text-xs text-[#555]">
                                              (alt: {f.alternatives.map(a => a.name).join(', ')})
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-1.5 text-right text-[#555]">{f.quantity || '—'}</td>
                                        <td className="py-1.5 text-right text-[#A3A3A3]">{f.calories || '—'}</td>
                                        <td className="py-1.5 text-right text-[#A3A3A3] hidden sm:table-cell">{f.protein ? `${f.protein}g` : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
